/**
 * Chat client — REST + LiveKit envelope plumbing.
 *
 * Why both: the live envelope (`chat/message`) makes the message appear on
 * the peer instantly; the REST round-trip is what makes it survive page
 * refresh, late-join recovery, and the audit log (apps.chat is the source
 * of truth, the envelope is an optimisation). Both ends listen on the bus
 * AND fetch via REST on mount — duplicates are dedup'd by message id.
 *
 * Operator and field both speak the same protocol. The /sessions/{id}/chat
 * endpoint is dual-auth (HasOrgPermission for operator, X-Field-Session-
 * Token for field) — see apps/chat/views.py.
 */
import axios from "axios";

import { ApiPaths, api } from "./api";
import { sha256Hex } from "./evidence";
import type { CommandBus } from "./commandBus";
import type {
  ChatMessage,
  ChatPage,
  Evidence,
  EvidenceInitResponse,
} from "./types";


export const CHAT_TOPIC = "chat" as const;
export const CHAT_MESSAGE_TYPE = "message" as const;


export type SendTextInput = {
  sessionId: string;
  body: string;
  attachmentIds?: string[];
  /** Optional client-minted id so optimistic UI stays in sync with the server row. */
  clientId?: string;
  /** Optional field-session token; when present, calls use the field path. */
  fieldSessionToken?: string;
  /** When set + bus is available, also publishes the envelope after the
   *  REST POST so the peer renders immediately instead of polling. */
  broadcastBus?: CommandBus | null;
};


/** REST list — page is bounded at the server (200 max); use `since` to
 *  page forward. */
export async function listChat(
  sessionId: string,
  opts: { since?: string; fieldSessionToken?: string } = {},
): Promise<ChatPage> {
  const url = ApiPaths.sessionChat(sessionId)
    + (opts.since ? `?since=${encodeURIComponent(opts.since)}` : "");
  if (opts.fieldSessionToken) {
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    const res = await axios.get<ChatPage>(`${apiBase}${url}`, {
      headers: { "X-Field-Session-Token": opts.fieldSessionToken },
    });
    return res.data;
  }
  const res = await api.get<ChatPage>(url);
  return res.data;
}


/** POST a text message + (optional) attachments + (optional) bus broadcast. */
export async function sendText(input: SendTextInput): Promise<ChatMessage> {
  const body = {
    body: input.body,
    attachment_ids: input.attachmentIds ?? [],
    id: input.clientId,
    envelope_msg_id: input.clientId, // dedup key on the server
  };
  let saved: ChatMessage;
  if (input.fieldSessionToken) {
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    const res = await axios.post<ChatMessage>(
      `${apiBase}${ApiPaths.sessionChat(input.sessionId)}`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          "X-Field-Session-Token": input.fieldSessionToken,
        },
      },
    );
    saved = res.data;
  } else {
    const res = await api.post<ChatMessage>(
      ApiPaths.sessionChat(input.sessionId),
      body,
    );
    saved = res.data;
  }

  // Best-effort live nudge so the peer doesn't wait on the next poll. The
  // peer's bus handler upserts by id; if the REST POST loses the race the
  // peer's own poll fills the gap.
  if (input.broadcastBus) {
    try {
      await input.broadcastBus.send({
        topic: CHAT_TOPIC,
        type: CHAT_MESSAGE_TYPE,
        payload: { message: saved },
      });
    } catch {
      /* envelope publish is the optimisation — REST is the source of truth */
    }
  }
  return saved;
}


/** Upload a file as an Evidence row so it can be attached to a chat
 *  message. Mirrors the snapshot upload pipeline: init → PUT →
 *  complete, with the file's bytes hashed client-side.
 *
 *  Kind is auto-detected from the file's MIME type:
 *    image/*       → kind="snapshot" (so the chat card renders the
 *                    📸 icon + the EvidenceViewerModal opens with the
 *                    image preview, instead of the document headline
 *                    + "open file" path)
 *    everything else → kind="document"
 *
 *  Fixes 2026-05-30 field bug where the technician shared a picture
 *  via the chat drawer and it showed up as a document. */
export async function uploadDocument(input: {
  sessionId: string;
  file: File;
  fieldSessionToken?: string;
}): Promise<Evidence> {
  const buffer = await input.file.arrayBuffer();
  const sha256 = await sha256Hex(buffer);
  const captured_at = new Date().toISOString();

  const mime = input.file.type || "application/octet-stream";
  const inferredKind = mime.startsWith("image/") ? "snapshot" : "document";

  const initBody = {
    kind: inferredKind,
    mime,
    byte_size: input.file.size,
    sha256,
    captured_at,
    geo: {},
    device: { name: input.file.name },
  };

  let init: EvidenceInitResponse;
  if (input.fieldSessionToken) {
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    const res = await axios.post<EvidenceInitResponse>(
      `${apiBase}${ApiPaths.sessionEvidenceInit(input.sessionId)}`,
      initBody,
      {
        headers: {
          "Content-Type": "application/json",
          "X-Field-Session-Token": input.fieldSessionToken,
        },
      },
    );
    init = res.data;
  } else {
    const res = await api.post<EvidenceInitResponse>(
      ApiPaths.sessionEvidenceInit(input.sessionId),
      initBody,
    );
    init = res.data;
  }

  // PUT bytes (skip if backend has no MinIO and minted a mock URL).
  if (!init.upload_url.startsWith("mock-s3://")) {
    await axios.put(init.upload_url, input.file, { headers: init.headers });
  }

  // Complete — this is what flips the row to ready + posts the chat
  // hook (NB: documents do NOT auto-post evidence cards; the attachment
  // chip in the user's text message is the surface).
  if (input.fieldSessionToken) {
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    const res = await axios.post<Evidence>(
      `${apiBase}${ApiPaths.evidenceComplete(init.evidence.id)}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          "X-Field-Session-Token": input.fieldSessionToken,
        },
      },
    );
    return res.data;
  }
  const res = await api.post<Evidence>(
    ApiPaths.evidenceComplete(init.evidence.id),
    {},
  );
  return res.data;
}
